import toast from "react-hot-toast";
import clipboardCopy from "clipboard-copy";
import { toastSuccess, toastFail } from "../../config";
import { Question, QuestionType, UserExtended } from "effno-core";
import { isEmptyJSON } from "../../utils/utils";

export const handleCopyText = async (text: string) => {
  try {
    await clipboardCopy(text);
  } catch (error) {
    console.error("Failed to copy text:", error);
    toast("Could not copy to clipboard", toastFail);
    return;
  }

  console.log("[handleCopyText] toastSUccess: ", toastSuccess);

  toast("Copied to clipboard", toastSuccess);
};

export function smartTimeAgo(sinceDate: Date, compressed: boolean = false) {
  const hoursInSeconds = 60 * 60;
  const daysInSeconds = 60 * 60 * 24;
  const fortnightInSeconds = 60 * 60 * 24 * 14;

  // get seconds
  let seconds = Math.floor((new Date().getTime() - sinceDate.getTime()) / 1000);

  if (seconds < 0) {
    seconds = 0;
  }

  if (seconds < 61) {
    return seconds + (compressed ? "s" : " seconds") + " ago";
  }

  if (seconds > 60 && seconds < 3601) {
    return Math.floor(seconds / 60) + (compressed ? "m" : " mins") + " ago";
  }

  if (seconds > hoursInSeconds && seconds < daysInSeconds + 1) {
    return (
      Math.floor(seconds / hoursInSeconds) +
      (compressed ? "h" : " hours") +
      " ago"
    );
  }

  if (seconds > daysInSeconds && seconds < fortnightInSeconds + 1) {
    return (
      Math.floor(seconds / daysInSeconds) +
      (compressed ? "d" : " days") +
      " ago"
    );
  }

  return (
    "on" +
    sinceDate.toLocaleDateString("en-us", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  );
}

export function smartTimeAgo2(sinceDate: Date, compressed: boolean = false) {
  const hoursInSeconds = 60 * 60;
  const daysInSeconds = 60 * 60 * 24;
  const fortnightInSeconds = 60 * 60 * 24 * 14;

  // get seconds
  let seconds = Math.floor((new Date().getTime() - sinceDate.getTime()) / 1000);

  if (seconds < 0) {
    seconds = 0;
  }

  if (seconds < 61) {
    return seconds + (compressed ? "s" : " seconds") + " ago";
  }

  if (seconds > 60 && seconds < 3601) {
    return Math.floor(seconds / 60) + (compressed ? "m" : " mins") + " ago";
  }

  if (seconds > hoursInSeconds && seconds < daysInSeconds + 1) {
    return (
      Math.floor(seconds / hoursInSeconds) +
      (compressed ? "h" : " hours") +
      " ago"
    );
  }

  if (seconds > daysInSeconds && seconds < fortnightInSeconds + 1) {
    return (
      Math.floor(seconds / daysInSeconds) +
      (compressed ? "d" : " days") +
      " ago"
    );
  }

  return sinceDate.toLocaleDateString("en-us", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}


// must be 8 chars+, 1+ caps, 1+ number
export const validatePassword = (p: string | undefined) => {
  if (!p) {
    return false;
  }

  const chars = p.length > 7;

  const capitalLettersPattern = /[A-Z]+/g;
  const capitalMatches = p.match(capitalLettersPattern) != null;

  const numberPattern = /\d+/g;
  const numberMatches = p.match(numberPattern) != null;

  return chars && capitalMatches && numberMatches;
};


export const themeIcon = (user: UserExtended | undefined, path: string) => {
  return `/icons/${(user?.theme) || "light-mode"}/${path}`;
}

export const getQuestionOrder = (questions: Question[] | undefined, questionOrderJSON: string | undefined) :
  { 
    nonEndQuestions: Question[],
    endQuestions: Question[]
  } => {

  // build order for regular and end questions
  const endQuestions: Question[] = [];
  const nonEndQuestions: Question[] = []; 

  if (!questions || !questionOrderJSON) {
    return {
      nonEndQuestions: [],
      endQuestions: [],
    }
  }

  const questionOrder = JSON.parse(questionOrderJSON);

  if (!isEmptyJSON(questionOrder)) {
      questionOrder.forEach((questionId: string) => {
          const question = questions.find((question: Question) => question.id === questionId);
          if (question) {
              if (question.type === QuestionType.END) {
                  endQuestions.push(question);
              } else {
                  nonEndQuestions.push(question);
              }
          }
      });
  }

  return {
    nonEndQuestions, 
    endQuestions,
  }

}

// JSON in, JSON out
export const addNewQuestionToOrder = (allQuestions: Question[], question: Question, questionOrderJSON?: string) => {
  // if and end question, put at the end
  // if not, put before the first end question
  let questionOrder: string[] = [];

  if (!questionOrderJSON || isEmptyJSON(JSON.parse(questionOrderJSON))) {
      questionOrder = [question.id];
  } else {
      questionOrder = JSON.parse(questionOrderJSON) as string[];

      if (question.type === QuestionType.END) {
          questionOrder.push(question.id);
      } else {
          // are there any end questions
          let endCount = 0;
          allQuestions.forEach((q: Question) => {
              if (q.type === QuestionType.END) {
                  endCount++;
              }
          });

          if (endCount === 0) {
              questionOrder.push(question.id);
          } else {
              let lowestEndIndex = 100000;
              allQuestions.filter((q: Question) => {
                  return q.type === QuestionType.END;
              }).forEach((endQ: Question) => {
                  const index = questionOrder.findIndex((endId: string) => endId === endQ.id);
                  if (index < lowestEndIndex) {
                      lowestEndIndex = index;
                  }
              });

              questionOrder.splice(lowestEndIndex, 0, question.id);
          }
      }
  }

  return(JSON.stringify(questionOrder));
}

// takes a hex and returns a rgb that is lighter by percentage
export function lightenColor(color: string, percentage: number) {
  // Convert hex color to RGB
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate the lighter color
  const newR = Math.min(255, Math.floor(r + (255 - r) * (percentage / 100)));
  const newG = Math.min(255, Math.floor(g + (255 - g) * (percentage / 100)));
  const newB = Math.min(255, Math.floor(b + (255 - b) * (percentage / 100)));

  // Return the new color in hex format

  // console.log(`original rgb: ${r}, ${g}, ${b}`);
  // console.log(`new rgb: ${newR}, ${newG}, ${newB}`);

  return `rgb(${newR}, ${newG}, ${newB})`;
}